import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ResizeEvent, ResizePhase, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed} from 'mobx';
import {observer, inject} from 'mobx-react';
import * as React from 'react';
import {AnySheet, SheetColumnName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';

const meshColumnHeaderStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(meshColumnHeaderStyles);

interface IMeshColumnHeaderProps
{
    ctx?:IWSPMeshContext;
    colIndex:number;
    isNested:boolean;
    theme?:IStyles;
}

@inject('ctx')
@observer
export class MeshColumnHeader extends React.Component<IMeshColumnHeaderProps, any>
{
    public componentWillMount():void
    {
        const {theme} = this.props;
        this._initStyles(theme);
    }

    public componentWillReceiveProps(newProps:IMeshColumnHeaderProps):void
    {
        const {theme} = this.props;

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(meshColumnHeaderStyles, theme || {})
        });
    }

    @computed
    private get _width():number
    {
        const {ctx, colIndex} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {colWidths, details: {resizingColIndex, resizingColWidth}} = meshState;

        return resizingColIndex === colIndex && resizingColWidth != null
            ? resizingColWidth
            : colWidths[colIndex];
    }

    @computed
    private get _style():React.CSSProperties
    {
        return {
            width: `${this._width}px`
        };
    }

    @computed
    private get _isSelected():boolean
    {
        const {ctx, colIndex} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {
            selectedRange: {
                from: {
                    colIndex: selectedColFrom
                },
                to: {
                    colIndex: selectedColTo
                }
            }
        } = meshState.details;

        return (colIndex >= selectedColFrom && colIndex <= selectedColTo)
            || (selectedColFrom == null && selectedColTo == null);
    }

    @action.bound
    private _onResize(event:ResizeEvent):void
    {
        const {ctx, colIndex} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {colWidths, details: {resizingColWidth}} = meshState;

        const newColWidth:number = (resizingColWidth != null
            ? resizingColWidth
            : colWidths[colIndex]) + event.deltaX;

        if( event.phase === ResizePhase.Stop )
        {
            meshState.setColWidth(colIndex, Math.max(10, newColWidth));
            meshState.resetResizingCol();

            controllers.ui.spreadsheets.mesh.calcRowHeightOnlyForWrappedCellsInColumn(ctx, colIndex);
        }
        else
            meshState.setResizingCol(colIndex, newColWidth);
    }

    @action.bound
    private _onResizerDblClick(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {ctx, colIndex, isNested} = this.props;

        event.preventDefault();
        event.stopPropagation();

        setTimeout(() =>
        {
            controllers.ui.spreadsheets.mesh.resizeColumnToContent(ctx, colIndex, isNested);
        });
    }

    @bind
    private _computeHeaderTitle():SheetColumnName
    {
        const {ctx, colIndex} = this.props;
        const {sheetId} = ctx;

        const sheet:AnySheet = controllers.ui.spreadsheets.main.findAnySheetById(sheetId, true);

        if( sheet.columnNames && colIndex >= 0 && colIndex < sheet.columnNames.length )
            return sheet.columnNames[colIndex];
        else
            return MeshLocationUtils.getLetterByColumnIndex(colIndex);
    }

    public render():JSX.Element
    {
        const {ctx, colIndex, isNested} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {size: {numCols}} = meshState;

        if( colIndex >= numCols )
            return null;

        return <div
            data-type={MeshViewTargetType.ColumnHeader}
            key={colIndex}
            style={this._style}
            className={cx(meshColumnHeaderStyles.tableHeader, {isSelected: this._isSelected}, {isNested})}
        >
            <span className={meshColumnHeaderStyles.tableColumnHeaderText}>
                {this._computeHeaderTitle()}
            </span>
            <Resizer
                isVisible={true}
                isVertical={true}
                className={meshColumnHeaderStyles.tableColumnHeaderResizer}
                onResize={this._onResize}
                onDblClick={this._onResizerDblClick}
            />
        </div>;
    }
}
